import { z } from "zod";
import i18n from "../config/i18n";

const t = (key: string) => i18n.t(key);

// Phone number regex
const PHONE_REGEX = /^\+?[0-9\-\s()]{7,20}$/;

// Code regex (alphanumeric with hyphens, underscores)
const CODE_REGEX = /^[A-Za-z0-9_-]+$/;

// Base schema for cargo company fields
const getBaseCargoCompanySchema = () =>
  z.object({
    name: z
      .string()
      .min(1, t("validation.companyNameRequired"))
      .min(2, t("validation.companyNameMin"))
      .max(100, t("validation.companyNameMax")),
    code: z
      .string()
      .min(1, t("validation.codeRequired"))
      .min(2, t("validation.codeMin"))
      .max(50, t("validation.codeMax"))
      .regex(CODE_REGEX, t("validation.codeFormat")),
    contactEmail: z
      .string()
      .min(1, t("validation.contactEmailRequired"))
      .email(t("validation.emailInvalid")),
    contactPhone: z
      .string()
      .optional()
      .refine(
        (val) => !val || PHONE_REGEX.test(val),
        t("validation.phoneInvalid"),
      ),
    address: z.string().optional(),
    isActive: z.boolean().default(true),
    settings: z.string().optional(),
  });

// Schema for creating cargo company
export const getCreateCargoCompanySchema = () => getBaseCargoCompanySchema();

// Schema for updating cargo company (all fields optional except validation rules)
export const getUpdateCargoCompanySchema = () =>
  z.object({
    name: z
      .string()
      .min(2, t("validation.companyNameMin"))
      .max(100, t("validation.companyNameMax"))
      .optional(),
    code: z
      .string()
      .min(2, t("validation.codeMin"))
      .max(50, t("validation.codeMax"))
      .regex(CODE_REGEX, t("validation.codeFormat"))
      .optional(),
    contactEmail: z
      .string()
      .email(t("validation.emailInvalid"))
      .optional(),
    contactPhone: z
      .string()
      .optional()
      .refine(
        (val) => !val || PHONE_REGEX.test(val),
        t("validation.phoneInvalid"),
      ),
    address: z.string().optional(),
    isActive: z.boolean().optional(),
    settings: z.string().optional(),
  });

// Schema for assigning user
export const getAssignUserSchema = () =>
  z.object({
    userId: z.string().min(1, t("validation.userIdRequired")),
    cargoCompanyId: z.string().min(1, t("validation.cargoCompanyIdRequired")),
  });

/** @deprecated Use getCreateCargoCompanySchema() for i18n support */
export const createCargoCompanySchema = getCreateCargoCompanySchema();
/** @deprecated Use getUpdateCargoCompanySchema() for i18n support */
export const updateCargoCompanySchema = getUpdateCargoCompanySchema();
/** @deprecated Use getAssignUserSchema() for i18n support */
export const assignUserSchema = getAssignUserSchema();

// Type exports
export type CreateCargoCompanyFormData = z.infer<
  ReturnType<typeof getCreateCargoCompanySchema>
>;
export type UpdateCargoCompanyFormData = z.infer<
  ReturnType<typeof getUpdateCargoCompanySchema>
>;
export type AssignUserFormData = z.infer<ReturnType<typeof getAssignUserSchema>>;

// Validation functions
export const validateCreateCargoCompany = (
  data: CreateCargoCompanyFormData,
) => {
  return getCreateCargoCompanySchema().safeParse(data);
};

export const validateUpdateCargoCompany = (
  data: UpdateCargoCompanyFormData,
) => {
  return getUpdateCargoCompanySchema().safeParse(data);
};

export const validateAssignUser = (data: AssignUserFormData) => {
  return getAssignUserSchema().safeParse(data);
};

// Get field errors from validation result
export const getFieldErrors = (result: z.SafeParseError<any>) => {
  const errors: Record<string, string> = {};
  result.error.errors.forEach((err) => {
    const path = err.path.join(".");
    errors[path] = err.message;
  });
  return errors;
};
